/**
 * Perplexity API Service
 * Used for SERP analysis and competitive research
 *
 * Sonar Model Family (December 2025):
 * - sonar: Lightweight grounded search (fast, cheap)
 * - sonar-pro: Deeper retrieval with follow-ups (recommended for SERP analysis)
 * - sonar-reasoning: Real-time reasoning with search
 * - sonar-reasoning-pro: Advanced reasoning powered by DeepSeek-R1
 * - sonar-deep-research: Long-form, source-dense reports
 *
 * Built on Llama 3.3 70B, runs at 1200 tokens/sec via Cerebras
 */

import { domainToSearchQuery } from '@utils/domainParser'

const PERPLEXITY_ENDPOINT = 'https://api.perplexity.ai/chat/completions'

// Available Sonar models
export const SONAR_MODELS = {
  'sonar': 'sonar',                         // Fast, lightweight search
  'sonar-pro': 'sonar-pro',                 // Deeper retrieval (recommended)
  'sonar-reasoning': 'sonar-reasoning',     // Reasoning + search
  'sonar-reasoning-pro': 'sonar-reasoning-pro', // Advanced reasoning (DeepSeek-R1)
  'sonar-deep-research': 'sonar-deep-research', // Long-form reports
}

// Search modes (March 2025 update)
export const SEARCH_MODES = {
  high: 'high',     // Maximum depth for complex queries
  medium: 'medium', // Balanced for moderately complex questions
  low: 'low',       // Cost-efficient while accurate
}

/**
 * Get stored Perplexity API key
 */
export function getApiKey() {
  const keys = JSON.parse(localStorage.getItem('emd_api_keys') || '{}')
  return keys.perplexity || null
}

/**
 * Store Perplexity API key
 */
export function setApiKey(key) {
  const keys = JSON.parse(localStorage.getItem('emd_api_keys') || '{}')
  keys.perplexity = key
  localStorage.setItem('emd_api_keys', JSON.stringify(keys))
}

/**
 * Check if Perplexity is configured
 */
export function isConfigured() {
  return !!getApiKey()
}

/**
 * Search with Perplexity
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Promise<Object>} Search results with citations
 */
export async function search(query, options = {}) {
  const apiKey = getApiKey()
  if (!apiKey) {
    throw new Error('Perplexity API key not configured')
  }

  const {
    model = 'sonar-pro', // Best for SERP analysis - deeper retrieval
    systemPrompt = 'You are a helpful search assistant. Provide factual, well-sourced information.',
    maxTokens = 4096,
    searchMode = 'medium', // high, medium, or low
  } = options

  const requestBody = {
    model: SONAR_MODELS[model] || model,
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: query },
    ],
    max_tokens: maxTokens,
    return_citations: true,
  }

  // Note: search_mode parameter removed - was causing issues with some Perplexity API versions
  // Can be re-enabled once verified with latest API docs

  const response = await fetch(PERPLEXITY_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify(requestBody),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error?.message || `Perplexity API error: ${response.status}`)
  }

  const data = await response.json()

  return {
    content: data.choices[0].message.content,
    citations: data.citations || [],
    usage: {
      inputTokens: data.usage?.prompt_tokens || 0,
      outputTokens: data.usage?.completion_tokens || 0,
    },
  }
}

const LANGUAGE_MARKETS = {
  fr: { name: 'France', locale: 'French' },
  en: { name: 'United States/UK', locale: 'English' },
  es: { name: 'Spain', locale: 'Spanish' },
  de: { name: 'Germany', locale: 'German' },
  it: { name: 'Italy', locale: 'Italian' },
  pt: { name: 'Portugal/Brazil', locale: 'Portuguese' },
}

/**
 * Analyze SERP for a domain/keyword
 * @param {string} domain - Domain to analyze (e.g., "plombier-paris-15.fr")
 * @param {Object} options - Options including language
 * @returns {Promise<Object>} SERP analysis
 */
export async function analyzeSERP(domain, options = {}) {
  const { language = 'fr' } = options
  const market = LANGUAGE_MARKETS[language] || LANGUAGE_MARKETS.fr

  // Use smart domain parser for better keyword extraction
  const queryInfo = domainToSearchQuery(domain)
  const keywords = queryInfo.main // e.g., "plombier Paris 15ème arrondissement"

  console.log('🔍 [PERPLEXITY] Domain:', domain)
  console.log('🔍 [PERPLEXITY] Parsed query:', keywords)
  console.log('🔍 [PERPLEXITY] Service:', queryInfo.service, '| Location:', queryInfo.location)

  const query = `
    Analyze the search results for "${keywords}" in ${market.name}:
    1. Who are the top 10 competitors for "${queryInfo.service}" ${queryInfo.location ? `in ${queryInfo.location}` : ''}?
    2. What content types rank well (guides, services pages, FAQs)?
    3. What are the main search intents (informational, transactional, local)?
    4. What topics/subtopics should a website cover?
    5. What questions do users commonly ask (PAA - People Also Ask)?

    Focus on the ${market.name} market. Provide specific, actionable insights in ${market.locale}.
  `

  const result = await search(query, {
    model: 'sonar-pro',  // Deeper retrieval for SERP analysis
    systemPrompt: `You are an SEO expert analyzing SERPs for local businesses in ${market.name}.
    Provide detailed, structured analysis with specific competitor names and content recommendations.
    Format your response in clear sections with bullet points.
    Respond in ${market.locale}.`,
  })

  return {
    domain,
    keywords,
    service: queryInfo.service,
    location: queryInfo.location,
    parsed: queryInfo.parsed,
    language,
    analysis: result.content,
    sources: result.citations,
    usage: result.usage,
  }
}

/**
 * Research a specific topic for content creation
 * @param {string} topic - Topic to research
 * @param {Object} context - Context (industry, target audience, etc.)
 * @returns {Promise<Object>} Research results
 */
export async function researchTopic(topic, context = {}) {
  const { industry, audience, location } = context

  const query = `
    Research topic: "${topic}"
    ${industry ? `Industry: ${industry}` : ''}
    ${audience ? `Target audience: ${audience}` : ''}
    ${location ? `Location: ${location}` : ''}

    Provide:
    1. Key facts and statistics
    2. Common questions people ask
    3. Expert opinions and best practices
    4. Related topics to cover
    5. Potential FAQs for a website
  `

  return search(query, {
    systemPrompt: `You are a content research assistant helping create website content.
    Provide accurate, up-to-date information with sources.
    Focus on information useful for creating comprehensive, SEO-friendly content.`,
  })
}

export default {
  search,
  analyzeSERP,
  researchTopic,
  getApiKey,
  setApiKey,
  isConfigured,
  SONAR_MODELS,
  SEARCH_MODES,
}
